import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { businessInfo } from "../data/mock";
import { ArrowLeft } from "lucide-react";

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Terms & Conditions - Trademark Brand Logo"
        description="Terms and Conditions for Trademark Brand Logo services. Review our service terms, user responsibilities, and legal agreements for trademark registration services."
        keywords="terms and conditions, service agreement, trademark services terms, legal terms"
      />
      <Header onOpenForm={() => {}} />
      
      <main className="pt-24 pb-20">
        <div className="container-custom px-4 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#1e3a8a] hover:text-[#d4af37] transition-colors duration-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>

          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
            <p className="text-gray-600">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 mb-8">
              <p className="text-gray-700 leading-relaxed">
                Welcome to {businessInfo.name}. These Terms and Conditions ("Terms") govern your use of our website 
                and services. By accessing our website or engaging our services, you agree to be bound by these Terms. 
                Please read them carefully.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using the {businessInfo.name} website and services, you acknowledge that you have 
                read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy. 
                If you do not agree with any part of these terms, you must not use our website or services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon 
                posting to our website. Your continued use of our services after any changes indicates your acceptance 
                of the modified Terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Services Description</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {businessInfo.name} provides trademark registration, copyright registration, company formation, 
                trademark search, and related intellectual property services. Our services include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>Comprehensive trademark and copyright searches</li>
                <li>Preparation and filing of trademark and copyright applications</li>
                <li>Correspondence with the United States Patent and Trademark Office (USPTO)</li>
                <li>Response to office actions and objections</li>
                <li>Trademark monitoring and enforcement assistance</li>
                <li>Company formation and business registration services</li>
                <li>Amazon Brand Registry enrollment assistance</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The specific services provided will depend on the package or service plan you select. All services 
                are subject to these Terms and any additional agreements or service contracts.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a client of {businessInfo.name}, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
                <li>
                  <strong>Provide Accurate Information:</strong> Supply complete, accurate, and truthful information 
                  for all trademark applications and services
                </li>
                <li>
                  <strong>Timely Communication:</strong> Respond promptly to requests for information, clarification, 
                  or documentation needed to process your applications
                </li>
                <li>
                  <strong>Use Rights:</strong> Confirm that you have the legal right to use and register the trademarks 
                  or copyrights for which you seek protection
                </li>
                <li>
                  <strong>Payment:</strong> Make timely payments for all services rendered according to the agreed-upon 
                  pricing and payment terms
                </li>
                <li>
                  <strong>Compliance:</strong> Comply with all applicable laws and regulations related to trademark 
                  and intellectual property use
                </li>
                <li>
                  <strong>Updates:</strong> Inform us of any changes to your contact information or business status 
                  that may affect your applications
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Fees and Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our service fees are clearly outlined on our website and in your service agreement. By engaging our 
                services, you agree to the following payment terms:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
                <li>
                  <strong>Service Fees:</strong> Our service fees are separate from and do not include government 
                  filing fees charged by the USPTO or other governmental agencies
                </li>
                <li>
                  <strong>Payment Due:</strong> Payment for services is due at the time of engagement unless otherwise 
                  agreed in writing
                </li>
                <li>
                  <strong>Government Fees:</strong> Government filing fees are non-refundable and are paid directly 
                  to the relevant governmental agency
                </li>
                <li>
                  <strong>Additional Services:</strong> Any services beyond the scope of your initial agreement may 
                  incur additional fees, which will be communicated to you in advance
                </li>
                <li>
                  <strong>Late Payments:</strong> Late payments may result in suspension of services and may incur 
                  late payment fees
                </li>
                <li>
                  <strong>Refund Policy:</strong> Refunds are subject to our refund policy and depend on the stage 
                  of service completion
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">5. No Guarantee of Registration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we use our best efforts and professional expertise to secure trademark and copyright registrations 
                for our clients, we cannot guarantee that your application will be approved by the USPTO or other 
                governmental agencies. Registration approval depends on various factors including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>Compliance with legal requirements and regulations</li>
                <li>Absence of conflicting prior registrations or applications</li>
                <li>Distinctiveness and registrability of the mark</li>
                <li>Proper classification and description of goods/services</li>
                <li>Response to office actions and objections</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We will work diligently to address any issues that arise during the application process, but final 
                approval decisions rest with the examining authorities.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, {businessInfo.name}, its officers, directors, employees, 
                and agents shall not be liable for any indirect, incidental, special, consequential, or punitive 
                damages arising from or related to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>Your use of or inability to use our services</li>
                <li>Denial or rejection of any trademark or copyright application</li>
                <li>Loss of business, profits, or opportunities</li>
                <li>Unauthorized access to or alteration of your data</li>
                <li>Third-party claims or actions</li>
                <li>Any other matter relating to our services</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Our total liability for any claims arising from our services shall not exceed the amount you paid 
                for the specific service giving rise to the claim.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on the {businessInfo.name} website, including text, graphics, logos, images, and software, 
                is the property of {businessInfo.name} or its licensors and is protected by copyright, trademark, and 
                other intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may not reproduce, distribute, modify, create derivative works from, publicly display, or exploit 
                any content from our website without our express written permission. Any trademarks, logos, or copyrights 
                you submit to us for registration remain your property, and we claim no ownership rights to them.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                We maintain strict confidentiality regarding all client information and trademark/copyright applications. 
                We will not disclose your information to third parties except as necessary to provide our services, 
                comply with legal obligations, or with your express consent. All employees and contractors are bound 
                by confidentiality agreements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Either party may terminate the service relationship under the following conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>
                  <strong>By Client:</strong> You may terminate services at any time by providing written notice. 
                  Refunds for terminated services will be subject to our refund policy
                </li>
                <li>
                  <strong>By Us:</strong> We may terminate services if you breach these Terms, fail to make required 
                  payments, provide false information, or engage in conduct that we deem inappropriate
                </li>
                <li>
                  <strong>Effect of Termination:</strong> Upon termination, you remain responsible for any outstanding 
                  fees, and we will provide you with copies of work completed up to the termination date
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any disputes arising from these Terms or our services shall be resolved as follows:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>
                  <strong>Informal Resolution:</strong> We encourage you to contact us first to attempt to resolve 
                  any disputes informally
                </li>
                <li>
                  <strong>Mediation:</strong> If informal resolution fails, both parties agree to attempt mediation 
                  before pursuing other legal remedies
                </li>
                <li>
                  <strong>Governing Law:</strong> These Terms shall be governed by the laws of the State of California, 
                  without regard to conflict of law principles
                </li>
                <li>
                  <strong>Jurisdiction:</strong> Any legal actions must be brought in the state or federal courts 
                  located in San Diego County, California
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Attorney-Client Relationship</h2>
              <p className="text-gray-700 leading-relaxed">
                Use of our website and initial consultations do not create an attorney-client relationship. An 
                attorney-client relationship is formed only when you formally engage our services by signing a 
                service agreement and making the required payment. All communications with our attorneys are subject 
                to attorney-client privilege once the relationship is established.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless {businessInfo.name}, its officers, directors, 
                employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including 
                reasonable attorneys' fees) arising from your use of our services, violation of these Terms, or 
                infringement of any third-party rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining 
                provisions shall continue in full force and effect. The invalid provision shall be modified to the 
                minimum extent necessary to make it valid and enforceable.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy and any service agreements you enter into with us, 
                constitute the entire agreement between you and {businessInfo.name} regarding our services and 
                supersede all prior agreements and understandings.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have questions about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gradient-primary text-white p-8 rounded-xl">
                <p className="mb-2"><strong>{businessInfo.name}</strong></p>
                <p className="mb-2">{businessInfo.address}</p>
                <p className="mb-2">Email: {businessInfo.email}</p>
                <p>Phone: {businessInfo.phone}</p>
              </div>
            </section>

            <div className="bg-gradient-to-r from-[#1e3a8a]/5 to-[#d4af37]/5 p-6 rounded-xl border-l-4 border-[#1e3a8a] mt-12">
              <p className="text-gray-700 leading-relaxed">
                <strong>By using our services, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms and Conditions.</strong>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions;
